import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { useAuth } from './AuthContext';

export const EMAIL_VERIFICATION_MESSAGE =
  'Verify your email to list a property, edit a submission, save favorites, and send an enquiry. You can keep browsing until then.';

export function useEmailVerification() {
  const { user, emailVerified } = useAuth();
  const blocked = user?.verificationRequired === true && !emailVerified;
  return { blocked };
}

/** Ask the header notice to draw attention when a locked action is attempted. */
export function promptEmailVerification() {
  window.dispatchEvent(new Event('email-verification-required'));
}

function ResendButton({ className }: { className: string }) {
  const { requestEmailVerification } = useAuth();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [ok, setOk] = useState(true);

  const resend = async () => {
    setSending(true);
    setMessage(null);
    const result = await requestEmailVerification();
    setOk(result.success);
    setMessage(
      result.success
        ? result.message || 'Verification email sent. Check your inbox and spam folder.'
        : result.error || 'Unable to send verification email. Please try again later.'
    );
    setSending(false);
  };

  return (
    <div className="flex flex-col items-start gap-1">
      <button type="button" onClick={resend} disabled={sending} className={className}>
        {sending ? 'Sending…' : 'Resend verification email'}
      </button>
      {message && (
        <p className={`text-xs ${ok ? 'text-amber-900' : 'text-red-700'}`}>{message}</p>
      )}
    </div>
  );
}

export function EmailVerificationNotice({ placement = 'card' }: { placement?: 'banner' | 'card' }) {
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    if (placement !== 'banner') return;
    const onNeed = () => {
      setHighlight(true);
      document.getElementById('email-verification-notice')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      window.setTimeout(() => setHighlight(false), 1600);
    };
    window.addEventListener('email-verification-required', onNeed);
    return () => window.removeEventListener('email-verification-required', onNeed);
  }, [placement]);

  if (placement === 'banner') {
    return (
      <div
        id="email-verification-notice"
        className={`border-t px-4 py-3 ${highlight ? 'bg-amber-200' : 'bg-amber-50'}`}
      >
        <div className="mx-auto max-w-6xl flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-2 text-sm text-amber-950">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{EMAIL_VERIFICATION_MESSAGE}</p>
          </div>
          <ResendButton className="shrink-0 text-sm font-medium text-amber-950 underline hover:text-amber-800 disabled:opacity-50" />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
      <div className="flex items-start gap-3">
        <Mail className="h-5 w-5 mt-0.5 text-amber-700 shrink-0" />
        <div>
          <h2 className="text-base font-semibold text-amber-950">Verify your email to continue</h2>
          <p className="mt-1 text-sm text-amber-950">{EMAIL_VERIFICATION_MESSAGE}</p>
          <div className="mt-3">
            <ResendButton className="text-sm font-medium text-white bg-amber-700 hover:bg-amber-800 rounded-md px-3 py-2 disabled:opacity-50" />
          </div>
        </div>
      </div>
    </div>
  );
}
